import { MiSearchAutocomplete } from "@miplatform/components";
import { useDebounce, useRouter } from "@miplatform/hooks";
import { IHome } from "@miplatform/types";
import { AutocompleteRenderInputParams, MenuItemProps } from "@mui/material";
import { useHomeSearchAutocomplete } from "../hooks/useHomeSearchAutocomplete";
import { HomeSearchOption } from "./HomeSearchOption";

export function HomeSearchAutocomplete({
  search,
  onSearchChange,
  onClearSearch,
  width = "440px",
  iconColor,
  renderInput,
  clearOnBlur = false,
  onEnterPress,
}: Readonly<HomeSearchAutocompleteProps>) {
  const router = useRouter();
  const debouncedSearch = useDebounce(search, 300);
  const { homeOptions, getHomeOptions, loading } = useHomeSearchAutocomplete({
    search: debouncedSearch,
  });

  const navigateToSearchHomes = () => {
    if (search.trim().length > 0) {
      router.push(`/homes/list?search=${encodeURIComponent(search.trim())}`);
    }
  };

  const handleKeyPress = () => {
    if (onEnterPress) {
      onEnterPress();
    } else {
      navigateToSearchHomes();
    }
  };

  return (
    <MiSearchAutocomplete
      label="Homes Search"
      placeholder="Search by Home Address, Customer Name, etc."
      search={search}
      debouncedSearch={debouncedSearch}
      options={homeOptions ?? []}
      onSearchChange={onSearchChange}
      renderOption={(props: MenuItemProps, option: IHome, onClose) => (
        <HomeSearchOption
          key={option.id}
          option={option}
          props={props}
          close={onClose}
        />
      )}
      width={width}
      loading={loading}
      getOptions={getHomeOptions}
      onEnterPress={handleKeyPress}
      renderInput={renderInput}
      iconColor={iconColor}
      onClearSearch={onClearSearch}
      clearOnBlur={clearOnBlur}
      getOptionLabel={(option) => option.address.addressLine1}
    />
  );
}

interface HomeSearchAutocompleteProps {
  search: string;
  onSearchChange: (value: string) => void;
  onClearSearch?: () => void;
  width?: string;
  onNavigate?: (id: string) => void;
  iconColor?: string;
  renderInput?: (params: AutocompleteRenderInputParams) => React.ReactNode;
  clearOnBlur?: boolean;
  onEnterPress?: () => void;
}
